import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import api from '@/api';
import { errorMessage } from '@/api/errors';
import { AlertBox } from '@/common/AlertBox';
import { TitleWithQuestion } from '@/common/TitleWithQuestion';
import {
  MemberTagForm,
  type MemberTagFormValues,
} from '@/groups/coordinator/MemberTagForm';
import { useMemberTaggingAlert } from '@/groups/coordinator/MemberTaggingAlertContext';
import { GroupHeaderWrapper } from '~/styles/layoutElements';

export const Route = createFileRoute(
  '/_app/_dashboard/groups/$groupName/admin/member-tagging/add',
)({
  component: AddMemberTagScreen,
});

function AddMemberTagScreen() {
  const { t } = useTranslation();
  const { groupName } = Route.useParams();
  const navigate = useNavigate();
  const { setFlash } = useMemberTaggingAlert();

  const { data: groupData } = api.getGroupBySlug.useQuery({
    path: { group_slug: groupName },
  });
  const groupId = groupData?.id ?? 0;

  const {
    mutateAsync: createTag,
    isPending,
    error,
  } = api.createGroupMemberTag.useMutation();

  const goToIndex = (): void => {
    navigate({
      to: '/groups/$groupName/admin/member-tagging/',
      params: { groupName },
    });
  };

  const handleSubmit = async (values: MemberTagFormValues): Promise<void> => {
    try {
      await createTag({
        path: { group_id: groupId },
        body: {
          label: values.tagName,
          student_ids: values.memberIds.map(Number),
        },
      });
      await api.listGroupMemberTags.invalidateQueries();
      // Writing the member set changes those students' `tags`, which is what
      // the group's All Students list renders.
      await api.getGroupStudents.invalidateQueries();
      setFlash('added');
      goToIndex();
    } catch {
      // Rendered from `error` below. The only code this operation adds is the
      // 409 `member_tag_label_taken`, and it has no legacy copy to port —
      // legacy has no uniqueness constraint at all, which is how its own
      // converter wrote six duplicate tags.
    }
  };

  return (
    <>
      <GroupHeaderWrapper>
        <TitleWithQuestion
          title={t('actions.add-member-tag')}
          helpKey="study_helper_group_settings"
        />
      </GroupHeaderWrapper>
      {error && (
        <AlertBox variant="error">
          {errorMessage(error, t('wbs.something-went-wrong-please'))}
        </AlertBox>
      )}
      <MemberTagForm
        groupId={groupId}
        submitLabel={t('actions.create')}
        isSubmitting={isPending}
        onBack={goToIndex}
        onSubmit={(values) => {
          void handleSubmit(values);
        }}
      />
    </>
  );
}
